import {BacnetPoint} from './point';
import {HubId} from './hub';
import {
  Tile,
  TILE_DEFAULT_WRITE_PRIORITY,
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_MULTISTATE,
  TileType,
} from './tile';
import {
  TILE_APPEARANCE_DEFAULT_DECIMAL,
  TILE_APPEARANCE_DEFAULT_INCREMENT,
  TILE_APPEARANCE_DEFAULT_MULTIPLIER,
} from './appearance';
import {TileStructure} from './structure';
import {TileIcon} from './tile-icon';
import {Device, DeviceId, DeviceType} from './device';

export interface BacnetPriorityArray {
  PointID: string;
  DeviceID: DeviceId;
  Values: { [priorityNumber: string]: null | string | number | boolean };
}

/**
 * This is only intended for use during the BACnet device discovery flow
 * or any flow specifically for configuring a BACnet device. All devices
 * must be turned into the more abstract Device type for general use.
 */
export interface BacnetDevice {
  Manufacturer: string;
  Model: string;
  NetAddress: string;
  Instance: number;
  Firmware: string;
  Error: boolean;
}

export interface BacnetDeviceJS extends BacnetDevice {
  Existing?: Device;
}

export interface BacnetDeviceDiscovery {
  HubID: HubId;
  Timeout: number;
}

export function makeFriendlyUnits(unitString: string): string {
  switch (unitString) {
    case 'degreesCelsius':
      return '°C';
    case 'degreesFahrenheit':
      return '°F';
    case 'degreesKelvin':
      return '°K';
    case 'percent':
    case 'percentRelativeHumidity':
      return '%';
    default:
      return unitString;
  }
}

export function makeFriendlyPointValue(point: BacnetPoint): string {
  if (point.Type.indexOf('multiState') === 0) {
    const index = multiStateValueIndex(+point.Value);
    if (point.UnitsState && typeof point.UnitsState[index] !== 'undefined') {
      return point.UnitsState[index];
    }
  }

  if (point.Type.indexOf('binary') === 0) {
    return point.UnitsState[+point.Value];
  }

  if (point.Type.indexOf('analog') === 0) {
    return '' + point.Value;
  }

  return '';
}

export function multiStateValueIndex(pointValue: number): number {
  return pointValue - 1;
}

export function determineTileType(point: BacnetPoint): TileType {
  switch (point.Type) {
    case 'binaryValue':
    case 'binaryInput':
    case 'binaryOutput':
      return TILE_TYPE_SIMPLE_BOOL;
    case 'analogValue':
    case 'analogInput':
    case 'analogOutput':
      return TILE_TYPE_SIMPLE_ANALOG;
    case 'multiStateValue':
    case 'multiStateInput':
    case 'multiStateOutput':
      return TILE_TYPE_SIMPLE_MULTISTATE;
    default:
      return '';
  }
}

export function makeTileFromBacnetPoint(
  point: BacnetPoint,
  type: TileType,
  writable: boolean,
  deviceId: DeviceId,
  icon: TileIcon = 'dot-circle',
): Tile {
  const tile = new Tile();

  tile.Name = point.Name;
  tile.Appearance = {
    Icon: icon,
    Color: '',
    Template: '',
    Config: {
      Decimal: TILE_APPEARANCE_DEFAULT_DECIMAL,
      Increment: TILE_APPEARANCE_DEFAULT_INCREMENT,
      Multiplier: TILE_APPEARANCE_DEFAULT_MULTIPLIER,
    },
  };
  tile.Type = type;

  let s: TileStructure;

  switch (tile.Type) {
    case TILE_TYPE_SIMPLE_BOOL:
      s = {
        Priority: TILE_DEFAULT_WRITE_PRIORITY,
        Protocol: DeviceType.BACnet,
        DeviceID: deviceId,
        PointID: JSON.stringify([point.Type, point.Address]),
        DisplayFalse: '',
        DisplayTrue: '',
        Writable: writable,
        Conditions: [],
        AutoClear: '',
      };

      // The device may have supplied verbiage for each of the bool states;
      // check and utilize here
      if (Array.isArray(point.UnitsState) && point.UnitsState.length === 2) {
        s.DisplayFalse = point.UnitsState[0];
        s.DisplayTrue = point.UnitsState[1];
      }

      tile.Structure = s;
      break;
    case TILE_TYPE_SIMPLE_ANALOG:
      let units: string = '';
      if (typeof point.UnitsState === 'string') {
        units = makeFriendlyUnits(point.UnitsState);
      }

      s = {
        Priority: TILE_DEFAULT_WRITE_PRIORITY,
        Protocol: DeviceType.BACnet,
        DeviceID: deviceId,
        PointID: JSON.stringify([point.Type, point.Address]),
        DisplayRawUnits: units,
        Writable: writable,
        Conditions: [],
        AutoClear: '',
      };

      tile.Structure = s;
      break;
    case TILE_TYPE_SIMPLE_MULTISTATE:
      let states: string[] = [];
      if (Array.isArray(point.UnitsState)) {
        states = point.UnitsState;
      }

      s = {
        Priority: TILE_DEFAULT_WRITE_PRIORITY,
        Protocol: DeviceType.BACnet,
        BuiltInStates: states,
        DisplayStates: [],
        DeviceID: deviceId,
        PointID: JSON.stringify([point.Type, point.Address]),
        Writable: writable,
        Conditions: [],
        AutoClear: '',
      };

      tile.Structure = s;
      break;
    default:
      throw new Error('no tile type found');
  }

  tile.setDefaults();
  return tile;
}
